import React from 'react';
import styled from 'styled-components';

import { H1 } from 'styles/typography';

type PageProps = {
  children: React.ReactNode;
};

const Page = ({ children }: PageProps) => {
  return <PageWrapper>{children}</PageWrapper>;
};

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const PageHeader = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
  flex: 0 0 auto;
`;

const PageSubHeader = styled.div`
  padding: 0 24px;

  > :first-child {
    margin-top: 24px;
  }
`;

const PageContent = styled.div`
  margin-top: 24px;
  padding: 0 24px 24px;
  flex: 1 1 auto;
  position: relative;
  overflow-y: auto;
`;

const PageTitle = styled(H1)`
  display: none;
  font-size: 20px;

  @media (min-width: 640px) {
    display: block;
  }
`;

// A sticky bottom action bar for mobile, as a flex sibling AFTER Page.Content rather than a
// position:fixed overlay. PageWrapper is already a full-height flex column with PageContent at
// `flex: 1 1 auto`, so a `flex: 0 0 auto` sibling pins itself for free — where position:fixed
// would escape the PageContent scroll chain and, on iOS Safari, sit under the collapsing toolbar.
// The bottom padding carries the home-indicator inset.
const PageMobileFooter = styled.div`
  display: none;

  @media ${({ theme }) => theme.devices.mobile} {
    display: flex;
    gap: 12px;
    flex: 0 0 auto;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    border-top: 1px solid ${({ theme }) => theme.colors.black10};
    background: ${({ theme }) => theme.colors.white};

    > * {
      flex: 1;
    }
  }
`;

Page.Header = PageHeader;
Page.SubHeader = PageSubHeader;
Page.Content = PageContent;
Page.Title = PageTitle;
Page.MobileFooter = PageMobileFooter;

export default Page;
